import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layout,
  theme,
  Table,
  Tag,
  Tabs,
  Typography,
  message,
  Alert,
  Button,
  Select,
  InputNumber,
  Space,
  Drawer,
  Spin,
  Modal,
  Descriptions,
} from "antd";
import { MailOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import LoadingOverlay from "react-loading-overlay-ts";
import dayjs from "dayjs";

import AdminTopBar from "../../components/TopBar/AdminTopbar";
import AdminSideBar from "../../components/SideBar/AdminSidebar";
import { AuthContext } from "../../context/AuthContext";
import {
  cancelEmailQueueApi,
  EmailQueueItem,
  EmailQueueStatus,
  EmailQueueType,
  flushEmailQueueApi,
  getEmailQueueApi,
  getEmailQueueByIdApi,
} from "../../services/Admin/EmailQueueApis";

const { Content } = Layout;

const STATUS_TABS: { key: EmailQueueStatus; label: string }[] = [
  { key: "QUEUED", label: "Queued" },
  { key: "SENT", label: "Sent" },
  { key: "FAILED", label: "Failed" },
  { key: "CANCELLED", label: "Cancelled" },
];

const STATUS_COLOR: Record<EmailQueueStatus, string> = {
  QUEUED: "orange",
  SENT: "green",
  FAILED: "red",
  CANCELLED: "default",
};

const EMAIL_TYPE_OPTIONS: { value: EmailQueueType; label: string }[] = [
  { value: "PENDING_LAPSE_RUN", label: "Pending Lapse Run" },
  { value: "LAPSED_SUMMARY", label: "Lapsed Summary" },
];

const recipientOf = (row: EmailQueueItem) => {
  if (row.toName && row.toEmail) return `${row.toName} <${row.toEmail}>`;
  return row.toEmail || row.toName || "—";
};

const subjectOf = (row: EmailQueueItem) => row.subject || "—";

const AdminEmailQueuePage: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const [isActive, setActive] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rows, setRows] = useState<EmailQueueItem[]>([]);
  const [sendEnabled, setSendEnabled] = useState<boolean | null>(null);
  const [status, setStatus] = useState<EmailQueueStatus>("QUEUED");
  const [emailType, setEmailType] = useState<EmailQueueType | undefined>();
  const [lapseRunId, setLapseRunId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerItem, setDrawerItem] = useState<EmailQueueItem | null>(null);

  const role = currentUser?.Role?.Name;
  const isSuperAdmin = role === "Super Admin";

  const loadQueue = useCallback(() => {
    setActive(true);
    getEmailQueueApi({
      status,
      ...(emailType ? { emailType } : {}),
      ...(lapseRunId != null ? { lapseRunId } : {}),
    })
      .then((res) => {
        if (!res || res.success === false) {
          message.error(res?.message || "Unable to load email queue.");
          setRows([]);
          setSendEnabled(null);
          return;
        }
        const data = res.data ?? {};
        setRows(Array.isArray(data.items) ? (data.items as EmailQueueItem[]) : []);
        setSendEnabled(
          typeof data.sendEnabled === "boolean" ? data.sendEnabled : null
        );
      })
      .catch((err) => {
        console.log(err);
        message.error("Unable to load email queue.");
        setRows([]);
      })
      .finally(() => {
        setActive(false);
        setSelectedIds([]);
      });
  }, [status, emailType, lapseRunId]);

  useEffect(() => {
    if (role && !isSuperAdmin) {
      navigate("/admin/dashboard");
    }
  }, [role, isSuperAdmin, navigate]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadQueue();
  }, [isSuperAdmin, loadQueue]);

  const openPreview = (record: EmailQueueItem) => {
    setDrawerOpen(true);
    setDrawerItem(null);
    setDrawerLoading(true);
    getEmailQueueByIdApi(record.id)
      .then((res) => {
        if (!res || res.success === false) {
          message.error(res?.message || "Unable to load email details.");
          return;
        }
        setDrawerItem((res.data as EmailQueueItem) || null);
      })
      .catch((err) => {
        console.log(err);
        message.error("Unable to load email details.");
      })
      .finally(() => setDrawerLoading(false));
  };

  const handleCancelSelected = () => {
    if (selectedIds.length === 0) {
      message.error("Select at least one queued email to cancel.");
      return;
    }
    Modal.confirm({
      title: "Cancel selected emails",
      content: `Mark ${selectedIds.length} queued email(s) as CANCELLED? They will not send on flush.`,
      okText: "Cancel emails",
      okButtonProps: { danger: true },
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = await cancelEmailQueueApi({ ids: selectedIds });
          if (!res || res.success === false) {
            message.error(res?.message || "Unable to cancel emails.");
            return Promise.reject();
          }
          message.success("Selected emails cancelled.");
          loadQueue();
        } catch (err) {
          console.log(err);
          message.error("Unable to cancel emails.");
          return Promise.reject();
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleFlushSelected = () => {
    if (selectedIds.length === 0) {
      message.error("Select at least one queued email to flush.");
      return;
    }
    Modal.confirm({
      title: "Flush selected emails",
      content: `Send ${selectedIds.length} queued email(s) via SMTP now?`,
      okText: "Flush",
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = await flushEmailQueueApi({ ids: selectedIds });
          if (!res || res.success === false) {
            message.error(
              res?.message ||
                "Unable to flush emails. Send may be disabled (409)."
            );
            return Promise.reject();
          }
          message.success("Selected emails flushed.");
          loadQueue();
        } catch (err: any) {
          console.log(err);
          message.error(
            err?.message ||
              "Unable to flush emails. Send may be disabled (409)."
          );
          return Promise.reject();
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleFlushAll = () => {
    Modal.confirm({
      title: "Flush all queued emails",
      content:
        "Send every QUEUED email via SMTP? This releases the full review backlog.",
      okText: "Flush all",
      okButtonProps: { danger: true },
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = await flushEmailQueueApi({});
          if (!res || res.success === false) {
            message.error(
              res?.message ||
                "Unable to flush emails. Send may be disabled (409)."
            );
            return Promise.reject();
          }
          message.success("All queued emails flushed.");
          loadQueue();
        } catch (err: any) {
          console.log(err);
          message.error(
            err?.message ||
              "Unable to flush emails. Send may be disabled (409)."
          );
          return Promise.reject();
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const columns: ColumnsType<EmailQueueItem> = [
    {
      title: "ID",
      dataIndex: "id",
      width: 80,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value: EmailQueueStatus) => (
        <Tag color={STATUS_COLOR[value] || "default"}>{value}</Tag>
      ),
    },
    {
      title: "Type",
      dataIndex: "emailType",
      render: (value?: string | null) => value || "—",
    },
    {
      title: "To",
      render: (_: unknown, row: EmailQueueItem) => recipientOf(row),
    },
    {
      title: "Subject",
      render: (_: unknown, row: EmailQueueItem) => subjectOf(row),
      ellipsis: true,
    },
    {
      title: "Lapse run",
      dataIndex: "lapseRunId",
      width: 100,
      render: (value?: number | null) => value ?? "—",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      render: (value?: string | null) =>
        value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "—",
    },
    {
      title: "Error",
      dataIndex: "errorMessage",
      ellipsis: true,
      render: (value?: string | null) => value || "—",
    },
  ];

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <LoadingOverlay active={isActive || actionLoading} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <AdminSideBar />
        <Layout className="site-layout">
          <AdminTopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <h2 style={{ margin: 0 }}>
                  <MailOutlined
                    style={{ paddingRight: "5px", color: "#f04f05" }}
                  />
                  Email Queue
                </h2>
                <Space wrap>
                  <Button
                    danger
                    disabled={status !== "QUEUED" || selectedIds.length === 0}
                    onClick={handleCancelSelected}
                  >
                    Cancel selected
                  </Button>
                  <Button
                    type="primary"
                    disabled={status !== "QUEUED" || selectedIds.length === 0}
                    onClick={handleFlushSelected}
                  >
                    Flush selected
                  </Button>
                  <Button
                    danger
                    type="primary"
                    disabled={status !== "QUEUED"}
                    onClick={handleFlushAll}
                  >
                    Flush all queued
                  </Button>
                </Space>
              </div>

              <Typography.Text type="secondary">
                Review lapse-related emails before they send. Open a row to
                preview HTML.
              </Typography.Text>

              {sendEnabled === false && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="warning"
                  showIcon
                  message="Send is disabled"
                  description="LAPSE_EMAIL_SEND_ENABLED is false. New emails stay QUEUED for review. Flush will fail with 409 until send is enabled."
                />
              )}
              {sendEnabled === true && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="info"
                  showIcon
                  message="Send is enabled"
                  description="New emails send on enqueue. Flush can still release any remaining QUEUED backlog."
                />
              )}

              <Space wrap style={{ marginTop: 16 }}>
                <Select
                  allowClear
                  placeholder="Email type"
                  style={{ width: 220 }}
                  value={emailType}
                  onChange={(value) => setEmailType(value)}
                  options={EMAIL_TYPE_OPTIONS}
                />
                <InputNumber
                  placeholder="Lapse run ID"
                  style={{ width: 160 }}
                  min={1}
                  value={lapseRunId ?? undefined}
                  onChange={(value) =>
                    setLapseRunId(typeof value === "number" ? value : null)
                  }
                />
              </Space>

              <Tabs
                type="card"
                style={{ marginTop: 16 }}
                activeKey={status}
                onChange={(key) => setStatus(key as EmailQueueStatus)}
                items={STATUS_TABS.map((tab) => ({
                  key: tab.key,
                  label: tab.label,
                }))}
              />

              <Table
                rowKey="id"
                columns={columns}
                dataSource={rows}
                pagination={{ pageSize: 20 }}
                rowSelection={
                  status === "QUEUED"
                    ? {
                        selectedRowKeys: selectedIds,
                        onChange: (keys) =>
                          setSelectedIds(keys.map((k) => Number(k))),
                      }
                    : undefined
                }
                onRow={(record) => ({
                  onClick: (event) => {
                    const target = event.target as HTMLElement;
                    if (
                      target.closest(".ant-checkbox-wrapper") ||
                      target.closest(".ant-table-selection-column")
                    ) {
                      return;
                    }
                    openPreview(record);
                  },
                  style: { cursor: "pointer" },
                })}
              />
            </div>
          </Content>
        </Layout>
      </Layout>

      <Drawer
        title={
          drawerItem
            ? `Email #${drawerItem.id}`
            : "Email preview"
        }
        width={720}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerItem(null);
        }}
      >
        {drawerLoading ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin />
          </div>
        ) : drawerItem ? (
          <>
            <Descriptions
              size="small"
              column={1}
              bordered
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[drawerItem.status] || "default"}>
                  {drawerItem.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {drawerItem.emailType || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="To">
                {recipientOf(drawerItem)}
              </Descriptions.Item>
              <Descriptions.Item label="Subject">
                {subjectOf(drawerItem)}
              </Descriptions.Item>
              <Descriptions.Item label="Lapse run">
                {drawerItem.lapseRunId ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {drawerItem.createdAt
                  ? dayjs(drawerItem.createdAt).format("YYYY-MM-DD HH:mm")
                  : "—"}
              </Descriptions.Item>
              {drawerItem.sentAt ? (
                <Descriptions.Item label="Sent">
                  {dayjs(drawerItem.sentAt).format("YYYY-MM-DD HH:mm")}
                </Descriptions.Item>
              ) : null}
              {drawerItem.flushedBy ? (
                <Descriptions.Item label="Flushed by">
                  {drawerItem.flushedBy}
                </Descriptions.Item>
              ) : null}
              {drawerItem.meta ? (
                <Descriptions.Item label="Meta">
                  <Typography.Text code style={{ whiteSpace: "pre-wrap" }}>
                    {drawerItem.meta}
                  </Typography.Text>
                </Descriptions.Item>
              ) : null}
              {drawerItem.errorMessage ? (
                <Descriptions.Item label="Error">
                  {drawerItem.errorMessage}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
            <Typography.Title level={5}>HTML body</Typography.Title>
            {drawerItem.htmlBody ? (
              <iframe
                title={`email-preview-${drawerItem.id}`}
                sandbox=""
                srcDoc={drawerItem.htmlBody}
                style={{
                  width: "100%",
                  height: 480,
                  border: "1px solid #f0f0f0",
                  borderRadius: 4,
                  background: "#fff",
                }}
              />
            ) : (
              <Typography.Text type="secondary">
                No HTML body available.
              </Typography.Text>
            )}
          </>
        ) : (
          <Typography.Text type="secondary">
            Unable to load email details.
          </Typography.Text>
        )}
      </Drawer>
    </LoadingOverlay>
  );
};

export default AdminEmailQueuePage;
